// Entrada SIMULADA con selector de rol. T-39, epica MG-ILE-471.
//
// ⚠ Esto NO es el login. El inicio de sesion real es Entra ID via MSAL (HU-32a, ADR-012,
// `lib/identidad.ts`): esta pantalla solo aparece mientras el ambiente no tiene configurado
// Entra (TT1-1) o cuando el build lo pide (`VITE_PERMITIR_LOGIN_SIMULADO=true`). De ahi el diseño:
//
//   · NO pide contraseña. Pedirla seria fingir una autenticacion que no existe, y
//     una contraseña falsa que "funciona" es lo primero que un usuario piloto
//     copia a produccion. Se elige un rol, y la pantalla dice con todas las letras
//     que es una simulacion.
//   · La eleccion se persiste via `lib/sesion.ts` (sessionStorage).
//   · HU-32a (32a-6): el MENU y el ATERRIZAJE ya no los decide el rol elegido. Los entrega
//     `GET /sesion` para el actor simulado del backend; el rol elegido es la lente con la
//     que se comportan las pantallas que deciden por rol.
//
// El lenguaje visual viene de la pantalla validada del prototipo (LoginScreen):
// panel de fotografia a sangre con titulares de valor + columna de entrada. Los
// degradados son NEUTROS (oscurecen, no tiñen) y cada foto lleva su
// object-position para que el sujeto quede en cuadro en el panel vertical.

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ShieldCheck, UserRound } from 'lucide-react';
import { ROLES } from '../data/navegacion';
import { Input, SUFIJO_DEL_TITULO } from '../components/ui';
import { Logotipo } from '../components/BrandMark';
import { iniciarSesionSimulada } from '../lib/sesion';
import img1 from '../assets/login/imagen_1.webp';
import img2 from '../assets/login/imagen_2.webp';
import img3 from '../assets/login/imagen_3.webp';
import type { RolDeNavegacion } from '../data/navegacion';
import type { UsuarioSimulado } from '../lib/sesion';

// `pos` = object-position: encuadra el sujeto de cada foto dentro del panel vertical. Va como
// clase de Tailwind (texto fijo, para que el escaner la encuentre) y no como `style`.
const SLIDES = [
  {
    src: img1, pos: 'object-[52%_45%]',
    titulo: 'Registra la sospecha donde ocurra',
    bajada: 'Formulario guiado, con evidencias y sin perder el borrador.',
    alt: 'Técnico registrando el formulario de notificación en una tablet dentro del almacén',
  },
  {
    src: img2, pos: 'object-[62%_45%]',
    titulo: 'El Comité decide con todo a la vista',
    bajada: 'Causalidad, conclusiones y acciones sobre un mismo expediente.',
    alt: 'Comité de Farmacovigilancia analizando la causalidad en una sala de reuniones',
  },
  {
    src: img3, pos: 'object-[42%_50%]',
    titulo: 'Notifica a la autoridad dentro del plazo',
    bajada: 'Formato Amarillo, carta y cargo, con el vencimiento siempre a la vista.',
    alt: 'Analista revisando el Formato Amarillo junto al cargo de la autoridad',
  },
];

type PropsDeLoginSimulado = {
  /**
   * Recibe el usuario elegido. El modulo de aterrizaje ya no lo decide esta pantalla: lo
   * entrega `GET /sesion` (`modulo_inicial`, HU-32a-C3).
   */
  onEntrar: (usuario: UsuarioSimulado) => void;
};

// ---------------------------------------------------------------------------------
// LA PLANTILLA «ENTRADA» (lineamiento UX/UI): panel de marca + columna de entrada.
//
// HU-32a (MG-ILE-63): la usan tambien el inicio de sesion con Entra (`Entrada.tsx`) y la
// pantalla «Sin acceso» (`SinAcceso.tsx`). Se exporta desde aqui y no desde `components/` a
// proposito: los indicadores de lamina son `<button>` propios (deuda registrada en el
// lineamiento, «Pendientes» 14) y moverlos a un archivo nuevo seria estrenar la deuda en otro
// sitio.
// ---------------------------------------------------------------------------------

function PanelDeMarca() {
  const [i, setI] = useState(0);

  useEffect(() => {
    // Guarda completa y no `window.matchMedia?.(…).matches`: en jsdom `matchMedia`
    // no existe y el encadenado opcional a medias revienta igual.
    const consulta =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    if (consulta?.matches) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % SLIDES.length), 6500);
    return () => clearInterval(t);
  }, []);

  return (
    // HU-51: el panel es una region complementaria con nombre (antes quedaba fuera de toda
    // region) y `superficie-oscura` pone en blanco el contorno de foco sobre la foto.
    <aside aria-label="Presentación del sistema"
      className="superficie-oscura relative hidden md:block overflow-hidden bg-marca-profundo select-none">
      {/* Cada slide es UNA CAPA con su foto y su texto: al fundirse, la imagen y el titular
          cambian juntos (ver la nota en el prototipo). */}
      {SLIDES.map((s, idx) => (
        <div key={s.src} aria-hidden={idx !== i}
          className={'absolute inset-0 transition-opacity duration-[900ms] ease-in-out ' +
            (idx === i ? 'opacity-100' : 'opacity-0 pointer-events-none')}>
          <img src={s.src} alt={idx === i ? s.alt : ''}
            className={'absolute inset-0 w-full h-full object-cover ' + s.pos + (idx === i ? ' animate-ken-burns' : '')} />
          <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-black/55 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 px-12 pb-24">
            {/* `p` y no `h1` (WCAG 1.3.1): eran tres H1 en la pantalla, uno por lamina. El
                encabezado principal es el de la columna de entrada. Mismo aspecto. */}
            <p className="font-display text-cifra font-semibold text-white max-w-[28.75rem]">
              {s.titulo}
            </p>
            <p className="text-body text-white/85 mt-3.5 leading-relaxed max-w-[26.25rem]">
              {s.bajada}
            </p>
          </div>
        </div>
      ))}

      {/* Marca e indicadores: fuera de las capas, no parpadean con la transicion */}
      <div className="absolute top-9 left-12 flex items-center gap-3.5 z-10">
        <Logotipo sobreFondoOscuro alto={30} conAire={false} />
        <span className="h-6 w-px bg-white/35" />
        <span className="text-meta font-medium text-white/80">Farmacovigilancia</span>
      </div>

      {/* HU-51: botones con `aria-current` y no `role=tab` (no hay paneles de pestaña:
          WCAG 4.1.2). El area pulsable mide 24 px de alto (2.5.8); la barra visible
          sigue siendo la linea fina de antes, centrada dentro. Pulsar hace lo mismo. */}
      <div className="absolute bottom-10 left-12 flex items-center gap-2 z-10">
        {SLIDES.map((s, idx) => (
          <button key={s.src} type="button" onClick={() => setI(idx)}
            aria-current={idx === i ? 'true' : undefined} aria-label={s.titulo}
            className={'h-6 min-w-6 flex items-center rounded-full ' + (idx === i ? 'w-10' : 'w-6')}>
            <span className={'h-1 rounded-full transition-all duration-300 ' +
              (idx === i ? 'w-10 bg-white' : 'w-4 bg-white/40 hover:bg-white/70')} />
          </button>
        ))}
      </div>
    </aside>
  );
}

/** La pantalla de entrada: panel de marca y, a la derecha, el contenido dentro de `main` y el pie. */
export function PantallaDeEntrada({ tituloDeLaPestana, pie, children, 'data-testid': testid }: {
  /** WCAG 2.4.2 (HU-51): la pestaña nombra la pantalla, como en el resto del sistema. */
  tituloDeLaPestana: string;
  /** El texto de la izquierda del pie. Sin texto, el pie lleva solo el copyright. */
  pie?: ReactNode;
  children: ReactNode;
  'data-testid'?: string;
}) {
  useEffect(() => { document.title = `${tituloDeLaPestana} · ${SUFIJO_DEL_TITULO}`; }, [tituloDeLaPestana]);

  return (
    <div data-testid={testid} className="h-[100dvh] w-full grid grid-cols-1 md:grid-cols-[1.05fr_1fr] bg-white overflow-hidden">
      <PanelDeMarca />

      {/* ---------- Columna de entrada ---------- */}
      <div className="min-h-0 h-full flex flex-col overflow-y-auto">
        {/* `main` (WCAG 1.3.1 / 2.4.1): la pantalla de entrada tiene su region principal. */}
        <main className="flex-1 flex flex-col justify-center px-8 sm:px-14 py-8">
          <div className="w-full max-w-[27.5rem] mx-auto">
            {/* Marca visible en movil, donde el panel de imagen no se muestra */}
            <div className="flex md:hidden items-center gap-2.5 mb-7">
              <Logotipo alto={28} conAire={false} />
              <span className="text-meta font-medium text-ink-soft">Farmacovigilancia</span>
            </div>
            {children}
          </div>
        </main>

        {/* Pie: ancla el panel y da el aire corporativo de un portal real */}
        <footer className="shrink-0 border-t border-hairline px-8 sm:px-14 py-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
          {pie && (
            <p className="flex items-center gap-1.5 text-meta text-ink-soft">
              <ShieldCheck size={12} /> {pie}
            </p>
          )}
          <p className="ml-auto text-meta text-ink-soft">
            © 2026 ILENDER PERU S.A. · Sistema de Farmacovigilancia
          </p>
        </footer>
      </div>
    </div>
  );
}

export default function LoginSimulado({ onEntrar }: PropsDeLoginSimulado) {
  const [nombre, setNombre] = useState('');

  const entrar = (rol: RolDeNavegacion) => {
    const usuario = iniciarSesionSimulada({ rol, nombre });
    // `null` = el rol no es uno de los conocidos y no se persistio nada (T-45). Los
    // botones se pintan desde `ROLES`, así que aquí no debería pasar nunca; si pasa,
    // lo correcto es quedarse en esta pantalla y no entrar sin identidad.
    if (!usuario) return;
    onEntrar(usuario);
  };

  return (
    <PantallaDeEntrada tituloDeLaPestana="Entrar">
      <h1 className="font-display text-pantalla font-semibold text-marca-profundo">Hola</h1>
      <p className="text-body text-ink-soft mt-1.5">
        Elige con qué rol quieres recorrer el sistema.
      </p>

      <div className="mt-5">
        <label htmlFor="nombre-simulado"
          className="block font-semibold text-meta text-ink-soft mb-1">
          Tu nombre <span className="font-normal">(opcional, firma los borradores)</span>
        </label>
        <Input id="nombre-simulado" value={nombre} maxLength={120}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Nombre y apellido" autoComplete="name" />
      </div>

      <p className="font-semibold text-meta text-ink-soft mt-6 mb-2 flex items-center gap-1.5">
        <UserRound size={13} /> Entrar como
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* `Object.entries` pierde el tipo de la clave y la devuelve como `string`.
            Se recupera aqui: los botones se pintan DESDE `ROLES`, asi que cada
            clave es por construccion un rol conocido. */}
        {(Object.entries(ROLES) as [RolDeNavegacion, typeof ROLES[RolDeNavegacion]][]).map(([rol, r]) => (
          <button key={rol} type="button" onClick={() => entrar(rol)}
            className="flex items-center gap-2.5 p-2.5 rounded-xl border border-control-borde bg-white
                       hover:border-marca hover:bg-marca-bruma/30 transition-all text-left">
            <span className="w-9 h-9 rounded-full bg-marca-profundo text-white text-meta font-bold
                             flex items-center justify-center shrink-0">
              {r.label.charAt(0)}
            </span>
            <span className="min-w-0">
              <span className="block text-meta font-semibold text-ink break-words">{r.label}</span>
              <span className="block text-meta text-ink-soft break-words">{r.desc}</span>
            </span>
          </button>
        ))}
      </div>
    </PantallaDeEntrada>
  );
}
